/**
 * 绑定建议打分器：综合多个信号为无标签数据位生成字段建议。
 *
 * 信号来源：
 *   1. 知识库匹配（label/position/pattern） → BindingSuggestion[]
 *   2. 样式特征（数字格式/边框/背景/对齐） → 数值信号
 *   3. 字段类型（金额/日期/数量/百分比/文本） → 启发式匹配
 *   4. 邻近关系（标签/其他数据位） → 距离衰减
 *
 * 打分公式（0..1）：
 *   confidence = w1*知识库信号 + w2*样式信号 + w3*类型匹配 + w4*邻近信号
 *   其中 w1=0.4, w2=0.3, w3=0.2, w4=0.1
 *
 * 输出：按置信度排序的前 N 个字段建议
 */

import type { SkeletonCell } from './extract.js';
import type { TemplateBlock } from './regions.js';
import {
  suggestBindings,
  type BindingDictLoader,
  type BindingSuggestion,
} from './bindingDict.js';

/**
 * 字段类型分类（启发式）
 */
export type FieldType =
  | 'amount'
  | 'date'
  | 'quantity'
  | 'percent'
  | 'text'
  | 'code'
  | 'unknown';

/**
 * 字段建议
 */
export interface FieldSuggestion {
  field: string;
  confidence: number;
  signals: {
    knowledgeBase: number;
    style: number;
    typeMatch: number;
    proximity: number;
  };
  reason: string[];
}

/**
 * 打分上下文
 */
export interface ScoringContext {
  row: number;
  col: number;
  label?: string;
  zone?: string;
  side?: string;
  style?: Record<string, unknown>;
  availableFields: string[];
  allCells: SkeletonCell[];
  blocks?: TemplateBlock[];
  /**
   * 参考报表锚定阈值：source==='userPattern' 且 confidence >= 该值时才锚定胜出。
   * 默认 USER_BIND_ANCHOR_THRESHOLD。
   */
  userAnchorThreshold?: number;
}

/**
 * 参考报表锚定阈值：参考报表（userDict）的精确绑定置信度 >= 该值时，
 * 视为"已确认绑定"，在建议中直接置顶胜出，不被其他启发式信号稀释。
 */
export const USER_BIND_ANCHOR_THRESHOLD = 0.9;

/**
 * 检测字段类型（从名称或标签推测）
 */
function inferFieldType(fieldName: string, label?: string): FieldType {
  const combined = `${fieldName}${label || ''}`.toLowerCase();

  if (/金额|价格|费用|成本|收入|销售|支付|合计|小计/.test(combined))
    return 'amount';
  if (/日期|时间|年月日/.test(combined)) return 'date';
  if (/数量|个数|份数|件/.test(combined)) return 'quantity';
  if (/比率|百分|%/.test(combined)) return 'percent';
  if (/代码|编码|号码|id/.test(combined)) return 'code';
  if (/名称|说明|描述|备注/.test(combined)) return 'text';

  return 'unknown';
}

/**
 * 计算样式特征信号（0..1）
 */
function computeStyleSignal(style?: Record<string, unknown>): number {
  if (!style) return 0;

  let score = 0;
  let weight = 0;

  // 检查数字格式
  const fmt = (style.format as string) || '';
  if (/[0#]|yyyy|mm|dd/i.test(fmt)) {
    score += 0.5;
    weight += 0.5;
  }

  // 检查边框
  const border = style.border as Record<string, unknown>;
  if (border) {
    const borderCount = Object.keys(border).filter((k) => border[k]).length;
    score += Math.min(borderCount / 4, 0.3);
    weight += 0.3;
  }

  // 检查背景色
  const bgcolor = (style.bgcolor as string) || '';
  if (
    bgcolor &&
    ![
      '#ffffff',
      '#f2f2f2',
      '#f9f9f9',
      '#dbe5f1',
      '#dae5f0',
    ].includes(bgcolor.toLowerCase())
  ) {
    score += 0.2;
    weight += 0.2;
  }

  return weight > 0 ? score / weight : 0;
}

/**
 * 计算类型匹配信号（字段类型与标签推测的类型是否匹配）
 */
function computeTypeMatchSignal(fieldName: string, label?: string): number {
  const fieldType = inferFieldType(fieldName);
  const labelType = inferFieldType('', label);

  // 精确匹配
  if (fieldType !== 'unknown' && fieldType === labelType) return 1.0;

  // 部分匹配（如 amount 与 price）
  const amountTypes = ['amount'];
  if (amountTypes.includes(fieldType) && amountTypes.includes(labelType))
    return 0.8;

  // 无关
  return 0;
}

/**
 * 计算邻近信号（标签/其他数据位的距离）
 */
function computeProximitySignal(
  row: number,
  col: number,
  label?: string,
  allCells?: SkeletonCell[],
): number {
  if (!label || !allCells) return 0;

  // 查找标签格位置
  const labelCell = allCells.find(
    (cell) => cell.text === label && cell.candidate === 'label',
  );
  if (!labelCell) return 0;

  const rowDist = Math.abs(row - labelCell.row);
  const colDist = Math.abs(col - labelCell.col);

  // 距离衰减：距离越近，信号越强
  // dist=0 → 1.0, dist=1 → 0.8, dist=2 → 0.5, dist≥3 → 0.1
  const dist = Math.max(rowDist, colDist);
  if (dist === 0) return 1.0;
  if (dist === 1) return 0.8;
  if (dist === 2) return 0.5;
  return 0.1;
}

/**
 * 主函数：为无标签数据位生成字段建议
 *
 * @param context 打分上下文（位置、标签、样式、可用字段等）
 * @param topN 返回前 N 个建议（默认 5）
 * @returns 排序后的字段建议列表
 */
export function scoreFieldSuggestions(
  context: ScoringContext,
  topN: number = 5,
  loader?: BindingDictLoader,
): FieldSuggestion[] {
  const {
    label,
    zone,
    side,
    style,
    availableFields,
    allCells,
  } = context;

  // 从知识库查询初始建议（优先使用调用方注入的 loader，如 CLI 动态提取的范例知识库）
  const knowledgeSuggestions = loader
    ? loader.query(label, zone, side)
    : suggestBindings(label, zone, side);

  // 参考报表精确绑定（source:'userPattern'）命中时直接锚定胜出：
  // 将其 confidence 置 1.0，不被样式/类型/邻近等启发式信号稀释或反超，
  // 使绑定结果更贴近参考报表。仅对注入 loader（参考报表知识库）的高置信精确绑定生效。
  const userAnchored = knowledgeSuggestions
    .filter(
      (s) =>
        s.source === 'userPattern' &&
        s.confidence >= (context.userAnchorThreshold ?? USER_BIND_ANCHOR_THRESHOLD) &&
        availableFields.includes(s.field),
    )
    .map((s) => ({
      field: s.field,
      confidence: 1.0,
    }))
    .sort((a, b) => b.confidence - a.confidence);

  if (userAnchored.length > 0) {
    return userAnchored.slice(0, topN).map((t) => ({
      field: t.field,
      confidence: t.confidence,
      signals: {
        knowledgeBase: t.confidence,
        style: 0,
        typeMatch: 0,
        proximity: 0,
      },
      reason: ['参考报表直接命中'],
    }));
  }

  const suggestions: FieldSuggestion[] = [];
  const fieldScores = new Map<string, FieldSuggestion>();

  // 处理知识库建议
  for (const kbSuggestion of knowledgeSuggestions) {
    if (!availableFields.includes(kbSuggestion.field)) continue;

    const styleSignal = computeStyleSignal(style);
    const typeSignal = computeTypeMatchSignal(kbSuggestion.field, label);
    const proximitySignal = computeProximitySignal(
      context.row,
      context.col,
      label,
      allCells,
    );

    const confidence =
      0.4 * kbSuggestion.confidence +
      0.3 * styleSignal +
      0.2 * typeSignal +
      0.1 * proximitySignal;

    const reason: string[] = [];
    if (kbSuggestion.confidence > 0.5) {
      reason.push(`知识库${kbSuggestion.source}匹配`);
    }
    if (styleSignal > 0.3) reason.push('样式特征');
    if (typeSignal > 0.5) reason.push('字段类型匹配');
    if (proximitySignal > 0.5) reason.push('标签邻近');

    fieldScores.set(kbSuggestion.field, {
      field: kbSuggestion.field,
      confidence,
      signals: {
        knowledgeBase: kbSuggestion.confidence,
        style: styleSignal,
        typeMatch: typeSignal,
        proximity: proximitySignal,
      },
      reason,
    });
  }

  // 对于知识库中没有的字段，仍然基于样式/类型/邻近进行打分
  for (const field of availableFields) {
    if (fieldScores.has(field)) continue;

    const styleSignal = computeStyleSignal(style);
    const typeSignal = computeTypeMatchSignal(field, label);
    const proximitySignal = computeProximitySignal(
      context.row,
      context.col,
      label,
      allCells,
    );

    const confidence =
      0.4 * 0 + // 知识库信号为 0
      0.3 * styleSignal +
      0.2 * typeSignal +
      0.1 * proximitySignal;

    // 只有综合置信度 ≥ 0.2 才作为候选
    if (confidence >= 0.2) {
      const reason: string[] = [];
      if (styleSignal > 0.3) reason.push('样式特征');
      if (typeSignal > 0.5) reason.push('字段类型匹配');
      if (proximitySignal > 0.5) reason.push('标签邻近');

      fieldScores.set(field, {
        field,
        confidence,
        signals: {
          knowledgeBase: 0,
          style: styleSignal,
          typeMatch: typeSignal,
          proximity: proximitySignal,
        },
        reason,
      });
    }
  }

  // 排序并取前 N 个
  suggestions.push(...Array.from(fieldScores.values()));
  suggestions.sort((a, b) => b.confidence - a.confidence);

  return suggestions.slice(0, topN);
}

/**
 * 批量打分：为多个无标签数据位生成建议
 */
export function batchScoreDataCells(options: {
  cells: SkeletonCell[];
  blocks?: TemplateBlock[];
  availableFields: string[];
  topN?: number;
  loader?: BindingDictLoader;
}): Map<string, FieldSuggestion[]> {
  const { cells, blocks, availableFields, topN = 5, loader } = options;

  const results = new Map<string, FieldSuggestion[]>();

  // 构建块索引
  const blockMap = new Map<string, TemplateBlock>();
  if (blocks) {
    for (const block of blocks) {
      for (let r = block.rowStart; r <= block.rowEnd; r++) {
        for (let c = block.colStart; c <= block.colEnd; c++) {
          blockMap.set(`${r},${c}`, block);
        }
      }
    }
  }

  // 查找所有标签
  const labelMap = new Map<number, string>();
  for (const cell of cells) {
    if (cell.candidate === 'label') {
      labelMap.set(cell.row, cell.text!);
    }
  }

  // 打分数据格
  for (const cell of cells) {
    if (cell.candidate !== 'data') continue;

    const block = blockMap.get(`${cell.row},${cell.col}`);
    const label = labelMap.get(cell.row);

    const context: ScoringContext = {
      row: cell.row,
      col: cell.col,
      label,
      zone: block?.zone,
      side: block?.side,
      style: cell.style,
      availableFields,
      allCells: cells,
      blocks,
    };

    const suggestions = scoreFieldSuggestions(context, topN, loader);
    results.set(`${cell.row},${cell.col}`, suggestions);
  }

  return results;
}
